import { Container } from '@clearcover/atlas';
import {
  DisplayCoverageType,
  GetPolicyByVersionIdResponse,
  VehicleCoveragesAndEndorsementsDiff,
  VehicleType,
  VersionedObjectDiff,
} from '@clearcover/core-app-types';
import {
  changeTypes,
  getCurrentData,
} from '../PolicyChangeCard/PolicyChangeCard';
import { useNewApi } from 'Shared/services/Api';
import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import { CoveragesAndEndorsementsDiff } from '../PolicyChangeCard/types';
import styles from './styles.module.css';
import { LoadingPage } from 'Shared/components';
import { formatDiff } from 'Policies/components/PolicyActivities/Common/Common';
import {
  InfoValuePairDataType,
  isDisplayValuePair,
} from 'Shared/components/Card/CardSectionInfoPairs/InfoPair';

type CoverageDetailsDiff = {
  versionId: string;
  label: string;
  diff: CoveragesAndEndorsementsDiff | VehicleCoveragesAndEndorsementsDiff;
  withLabel?: boolean;
};

type CoverageWithDiffType = DisplayCoverageType & {
  type: keyof typeof changeTypes;
};

export const PolicyChangeCoverageDetails: FC<CoverageDetailsDiff> = ({
  versionId,
  label,
  diff,
  withLabel = true,
}) => {
  const { id: policyId } = useParams<{
    id: string;
  }>();
  const [{ data, loading }] = useNewApi<GetPolicyByVersionIdResponse>(
    {
      url: `/policies/${policyId}/version/${versionId}`,
    },
    true,
    false
  );

  if (loading) {
    return <LoadingPage />;
  }

  const { policyCoverages, policyEndorsements, vehicles } = data ?? {
    policyCoverages: [],
    policyEndorsements: [],
    vehicles: [],
  };

  const allCoverages = isVehicleCoveragesAndEndorsementsDiff(diff)
    ? combineVehicleCoverages(diff, vehicles)
    : combineCoverages(diff, [...policyCoverages, ...policyEndorsements]);

  const getUpdatedDiffDisplayValue = (
    coverage: CoverageWithDiffType
  ): React.ReactNode => {
    const resultDisplayValue = (
      fomattedDiff: InfoValuePairDataType
    ): React.ReactNode =>
      isDisplayValuePair(fomattedDiff) ? fomattedDiff.display : fomattedDiff;

    // if Adding new Coverage no old value is available refer to User Story at https://clearcover.atlassian.net/browse/PIE-402
    if (coverage.type === 'ADDED') {
      const fomattedDiff = formatDiff('No', coverage.displayValue);

      return resultDisplayValue(fomattedDiff);
    }

    const matchCoverageIdentifierFromOldDiff = ({
      old,
    }: VersionedObjectDiff<DisplayCoverageType>) =>
      old?.coverageIdentifier === coverage.coverageIdentifier;

    const oldData =
      diff.coverages.find(matchCoverageIdentifierFromOldDiff)?.old ||
      diff.endorsements.find(matchCoverageIdentifierFromOldDiff)?.old;

    if (oldData) {
      const fomattedDiff = formatDiff(
        oldData.displayValue,
        coverage.type === 'DELETED' ? 'Removed' : coverage.displayValue
      );

      return resultDisplayValue(fomattedDiff);
    }

    return coverage.displayValue;
  };

  return (
    <div data-testid="coverage-details">
      <Container>
        {withLabel && <h4 data-testid="coverage-details-label">{label}</h4>}
        <div className={styles.table}>
          {[...allCoverages].map((coverage, index) => (
            <div
              key={coverage.label}
              className={styles.coverage}
              data-testid={`coverage-details-${index}`}
            >
              <div>{coverage.label}</div>
              <div className={styles.coverageValue}>
                <>{getUpdatedDiffDisplayValue(coverage)}</>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
};

function isVehicleCoveragesAndEndorsementsDiff(
  diff: CoveragesAndEndorsementsDiff | VehicleCoveragesAndEndorsementsDiff
): diff is VehicleCoveragesAndEndorsementsDiff {
  return 'insurableItemIdentifier' in diff;
}

function combineVehicleCoverages(
  diff: VehicleCoveragesAndEndorsementsDiff,
  vehicles: Array<VehicleType>
) {
  const vehicle = vehicles.find(
    vehicle => vehicle.identifier === diff.insurableItemIdentifier
  ) ?? {
    coverages: [],
    endorsements: [],
  };
  return combineCoverages(diff, [
    ...vehicle.coverages,
    ...vehicle.endorsements,
  ]);
}

function combineCoverages(
  diff: CoveragesAndEndorsementsDiff | VehicleCoveragesAndEndorsementsDiff,
  existingCoverages: Array<DisplayCoverageType>
): Array<CoverageWithDiffType> {
  const activityCoverages = toCoveragesWithTypes([
    ...diff.coverages,
    ...diff.endorsements,
  ]);
  const existingAndChangedCoverages: Array<CoverageWithDiffType> = existingCoverages.map(
    existingCoverage =>
      activityCoverages.find(sameIdentifierAs(existingCoverage)) ?? {
        type: 'UPDATED',
        ...existingCoverage,
      }
  );
  const newCoverages = activityCoverages.filter(
    it => !existingAndChangedCoverages.some(sameIdentifierAs(it))
  );
  return [...existingAndChangedCoverages, ...newCoverages];
}

function sameIdentifierAs(targetCoverage: DisplayCoverageType) {
  return (coverage: DisplayCoverageType) =>
    coverage.coverageIdentifier === targetCoverage.coverageIdentifier;
}

function toCoveragesWithTypes(
  diffs: Array<VersionedObjectDiff<DisplayCoverageType>>
): Array<CoverageWithDiffType> {
  const coveragesWithTypes: Array<CoverageWithDiffType> = [];
  diffs.forEach(diff => {
    switch (diff.type) {
      case 'ADDED':
        coveragesWithTypes.push({ ...getCurrentData(diff), type: 'ADDED' });
        break;
      case 'DELETED':
        coveragesWithTypes.push({ ...getCurrentData(diff), type: 'DELETED' });
        break;
      case 'UPDATED':
        coveragesWithTypes.push({ ...getCurrentData(diff), type: 'UPDATED' });
        break;
    }
  });
  return coveragesWithTypes;
}
